import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { Crown, Check, Loader2, ShieldCheck } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { supabase } from "../lib/supabase";
import { openCheckout, openCheckoutForTransaction, cancelSubscription, resumeSubscription } from "../lib/paddle";
import { toast } from "sonner";

// Glow & Grow prices — USD only, worldwide. No currency detection.
// $12/month, $89/year (≈ $7.42/month, save 38%).
const CURRENCY = { symbol: "$", mo: 12, yr: 89, code: "USD" };

const FEATURES = [
  { bg: "#FDE8F0", emoji: "🔍", name: "Dupe Finder", desc: "— affordable swaps matched to your profile" },
  { bg: "#E8F0FD", emoji: "✨", name: "Daily Insights", desc: "— from a dermatologist + trichologist" },
  { bg: "#FDE8F0", illustration: "spa", name: "Adaptive routines", desc: "— that evolve with your skin and hair weekly" },
  { bg: "#E8FDF0", emoji: "🏆", name: "Premium Glow Challenges", desc: "— with collectible badges" },
  { bg: "#F5E8FD", emoji: "📈", name: "Glow Score analytics", desc: "— and progress trends" },
  { bg: "#FDE8E8", emoji: "📚", name: "Premium DIY recipes", desc: "— tailored to your profile" },
];

export default function Premium() {
  const { profile, refreshProfile } = useAuth();
  const [plan, setPlan] = useState("yearly");
  const [busy, setBusy] = useState(false);
  const [showCancel, setShowCancel] = useState(false);
  const [cancelBusy, setCancelBusy] = useState(false);
  // Post-checkout activation state — set when Paddle fires checkout.completed.
  const [activating, setActivating] = useState(false);
  const [activationFailed, setActivationFailed] = useState(false);
  const cur = CURRENCY;
  const monthlyEquivalent = 7.42;
  const savings = 38;

  // Poll the profile until the webhook flips premium/trial on. Returns the
  // fresh profile if confirmed, else null after the timeout.
  const pollForPremium = async (tries = 12, intervalMs = 2500) => {
    for (let i = 0; i < tries; i++) {
      await new Promise((r) => setTimeout(r, intervalMs));
      const fresh = await refreshProfile();
      const activeTrial =
        fresh?.trial_end_at && new Date(fresh.trial_end_at).getTime() > Date.now();
      if (fresh?.is_premium || activeTrial) return fresh;
    }
    return null;
  };

  // Cancel-at-period-end flow. Now goes through the paddle-billing Edge
  // Function which calls Paddle's Subscriptions API server-side and lets the
  // webhook update our DB with the real current_period_end.
  const requestCancel = async () => {
    if (!profile?.id) return;
    setCancelBusy(true);
    try {
      await cancelSubscription();
      // Poll the profile until the webhook flips cancel_at_period_end so the
      // UI reflects the cancellation immediately, no manual refresh.
      for (let i = 0; i < 10; i++) {
        await new Promise((r) => setTimeout(r, 1500));
        const fresh = await refreshProfile();
        if (fresh?.cancel_at_period_end) break;
      }
      setShowCancel(false);
      toast.success("Cancellation scheduled — you keep premium until the end of your billing period.");
    } catch (e) {
      toast.error(`Couldn't cancel: ${e.message}`);
    } finally {
      setCancelBusy(false);
    }
  };

  // Resume a scheduled cancellation.
  const requestResume = async () => {
    if (!profile?.id) return;
    setBusy(true);
    try {
      await resumeSubscription();
      for (let i = 0; i < 10; i++) {
        await new Promise((r) => setTimeout(r, 1500));
        const fresh = await refreshProfile();
        if (!fresh?.cancel_at_period_end) break;
      }
      toast.success("Welcome back — your subscription will keep renewing.");
    } catch (e) {
      toast.error(`Couldn't resume: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  // Real Paddle checkout — overlay on all devices. Success is confirmed by
  // the webhook flipping is_premium server-side, NEVER by a URL param.
  const startCheckout = async () => {
    setBusy(true);
    try {
      await openCheckout(plan);
    } catch (e) {
      toast.error(e.message || "Couldn't open checkout. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  // Mount-time: handle two cases cleanly.
  //   1. URL has `_ptxn=txn_...` (user arrived via a Paddle return-redirect) —
  //      re-open the Paddle overlay for that transaction so the card form
  //      actually appears.
  //   2. URL has `checkout=success` — DO NOT treat as proof of payment.
  //      Just strip the param. Real activation is a webhook event (poll
  //      the profile briefly in case webhook arrives in the next few seconds).
  useEffect(() => {
    const url = new URL(window.location.href);
    const ptxn = url.searchParams.get("_ptxn");
    const stale = url.searchParams.get("checkout");
    if (!ptxn && !stale) return;
    url.searchParams.delete("_ptxn");
    url.searchParams.delete("checkout");
    window.history.replaceState({}, "", url.toString());

    if (ptxn) {
      // Re-open the Paddle overlay to actually collect the card.
      openCheckoutForTransaction(ptxn).catch((e) =>
        toast.error(e?.message || "Couldn't reopen checkout — please try again.")
      );
      return;
    }

    // Only here when ?checkout=success was present without a transaction id.
    // Poll briefly in case the webhook is in flight; otherwise show an
    // HONEST message — not a false success.
    let cancelled = false;
    (async () => {
      for (let i = 0; i < 10 && !cancelled; i++) {
        await new Promise((r) => setTimeout(r, 2000));
        const fresh = await refreshProfile();
        if (fresh?.is_premium) {
          toast.success("You're glowing in Premium ✨");
          return;
        }
      }
      if (cancelled) return;
      toast.error(
        "We couldn't confirm your upgrade. If you were charged, contact charyl@glowgrow.app and we'll sort it out.",
        { duration: 12000 },
      );
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Paddle fires `gg:checkout-completed` on the window (from lib/paddle.js's
  // Paddle.initialize eventCallback). Poll the DB briefly so the UI flips to
  // Premium/trial without needing a manual refresh.
  useEffect(() => {
    const onCompleted = async () => {
      setActivationFailed(false);
      setActivating(true);
      const fresh = await pollForPremium();
      setActivating(false);
      if (fresh) {
        toast.success("You're glowing in Premium ✨");
      } else {
        setActivationFailed(true);
      }
    };
    window.addEventListener("gg:checkout-completed", onCompleted);
    return () => window.removeEventListener("gg:checkout-completed", onCompleted);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Hide the fixed BottomNav + lock background scroll while the cancel modal
  // is open. Index.css carries the matching rule for `.gg-modal-open`.
  useEffect(() => {
    if (!showCancel) return;
    document.body.classList.add("gg-modal-open");
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.classList.remove("gg-modal-open");
      document.body.style.overflow = prevOverflow;
    };
  }, [showCancel]);

  return (
    <div className="-mt-px" data-testid="premium-page">
      {/* HEADER — pink-to-cocoa gradient with decorative floating product graphics */}
      <div className="relative overflow-hidden pt-7 px-6 pb-9" data-testid="premium-header">
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, #C9748A 0%, #B85E75 45%, #6B2A1C 80%, #2E1608 100%)",
          }}
        />
        <FloatingGraphics />

        <div className="relative text-center">
          <div className="text-4xl mb-3" style={{ textShadow: "0 2px 8px rgba(0,0,0,0.25)" }}>
            👑
          </div>
          <h1
            className="font-serif text-white text-[34px] leading-[1.1] tracking-tight mb-3"
            style={{ textShadow: "0 2px 12px rgba(0,0,0,0.3)" }}
          >
            Become the most cared-for version of{" "}
            <span className="text-[#C9975A]">you.</span>
          </h1>
          <p
            className="text-white/85 text-sm font-light leading-snug max-w-[300px] mx-auto"
            style={{ textShadow: "0 1px 6px rgba(0,0,0,0.25)" }}
          >
            A complete personalized beauty operating system — built for you.
          </p>
        </div>
      </div>

      {/* CONTENT */}
      <div className="px-5 pt-6 pb-24">
        {/* Trial activation banner — webhook-flip in progress or stalled. */}
        {(activating || activationFailed) && (
          <div
            className="rounded-2xl p-4 mb-5 flex items-center gap-3"
            style={{ background: "#FDECEF", border: "1px solid #C9748A" }}
            data-testid="activation-banner"
          >
            {activating ? (
              <>
                <Loader2 size={18} className="animate-spin text-rose shrink-0" />
                <p className="text-[13px] text-cocoa font-medium leading-snug">
                  Activating your trial…
                  <span className="block text-[11px] font-light text-cocoa/65 mt-0.5">
                    This usually takes a few seconds. Hold tight — no need to refresh.
                  </span>
                </p>
              </>
            ) : (
              <>
                <div className="flex-1">
                  <p className="text-[13px] text-cocoa font-medium leading-snug">
                    We couldn&apos;t confirm your trial yet.
                  </p>
                  <p className="text-[11px] font-light text-cocoa/65 mt-0.5">
                    If you completed checkout, tap refresh. Still stuck? Email charyl@glowgrow.app.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => window.location.reload()}
                  data-testid="activation-refresh-btn"
                  className="rounded-full bg-rose hover:bg-rose-dark text-white text-xs font-medium px-4 py-2 shrink-0"
                >
                  Refresh
                </button>
              </>
            )}
          </div>
        )}

        {/* Features list */}
        <div className="space-y-4 mb-6" data-testid="features-list">
          {FEATURES.map((f, i) => (
            <div
              key={i}
              className="flex items-start gap-3"
              data-testid={`feature-${i}`}
            >
              <div
                className="w-[42px] h-[42px] rounded-xl flex items-center justify-center text-xl shrink-0"
                style={{ background: f.bg }}
              >
                {f.illustration === "spa" ? <SpaWomanIcon /> : <span>{f.emoji}</span>}
              </div>
              <p className="text-[13px] leading-snug pt-1.5" style={{ color: "#2E1608" }}>
                <strong className="font-semibold">{f.name}</strong>{" "}
                <span className="font-light">{f.desc}</span>
              </p>
            </div>
          ))}
        </div>

        {profile?.is_premium ? (
          <>
            {(() => {
              const trialEnd = profile?.trial_end_at ? new Date(profile.trial_end_at) : null;
              const inTrial = trialEnd && trialEnd.getTime() > Date.now();
              const daysLeft = inTrial
                ? Math.max(1, Math.ceil((trialEnd.getTime() - Date.now()) / 86400000))
                : null;
              const cancelled = !!profile?.cancel_at_period_end;
              return (
                <div
                  className="rounded-2xl p-6 text-center text-white"
                  style={{
                    background: inTrial
                      ? "linear-gradient(135deg, #B85E75 0%, #C9748A 100%)"
                      : "linear-gradient(135deg, #C9975A 0%, #E3BA85 100%)",
                  }}
                  data-testid="already-premium"
                >
                  <Crown className="mx-auto mb-2" />
                  {inTrial ? (
                    <>
                      <h3 className="font-serif text-2xl mb-1" data-testid="trial-active-title">
                        You&apos;re in your free trial ✨
                      </h3>
                      <p className="text-sm font-light text-white/90" data-testid="trial-days-left">
                        {cancelled ? (
                          <>
                            Trial cancelled. Access ends {trialEnd.toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                            {" "}— no charge.
                          </>
                        ) : (
                          <>
                            <strong className="font-semibold">{daysLeft} day{daysLeft === 1 ? "" : "s"} left</strong>{" "}
                            — you&apos;ll be charged on {trialEnd.toLocaleDateString(undefined, { month: "short", day: "numeric" })} unless you cancel.
                          </>
                        )}
                      </p>
                    </>
                  ) : (
                    <>
                      <h3 className="font-serif text-2xl mb-1">You&apos;re glowing in Premium ✨</h3>
                      <p className="text-sm font-light text-white/85">
                        {cancelled ? (
                          <>
                            Cancellation scheduled. You&apos;ll keep access until{" "}
                            {profile?.premium_until
                              ? new Date(profile.premium_until).toLocaleDateString(undefined, {
                                  month: "short", day: "numeric", year: "numeric",
                                })
                              : "the end of your billing period"}.
                          </>
                        ) : (
                          <>All features unlocked. Enjoy the ritual.</>
                        )}
                      </p>
                    </>
                  )}
                </div>
              );
            })()}

            {/* Cancel subscription — only visible when NOT already cancelled */}
            {!profile?.cancel_at_period_end && (
              <div className="mt-6 text-center">
                <button
                  type="button"
                  onClick={() => setShowCancel(true)}
                  data-testid="cancel-subscription-btn"
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-cocoa/55 hover:text-cocoa/80 underline underline-offset-4 decoration-cocoa/20"
                >
                  {profile?.trial_end_at && new Date(profile.trial_end_at).getTime() > Date.now()
                    ? "Cancel trial"
                    : "Cancel subscription"}
                </button>
                <p className="text-[10px] text-cocoa/40 font-light mt-2 max-w-xs mx-auto leading-relaxed">
                  {profile?.trial_end_at && new Date(profile.trial_end_at).getTime() > Date.now()
                    ? "Cancel any time before your trial ends and you won't be charged. You keep access through the trial period."
                    : "Cancel anytime — you'll keep your premium access until the end of your current billing period."}
                </p>
              </div>
            )}

            {/* Resume — visible only when a cancellation is scheduled */}
            {profile?.cancel_at_period_end && (
              <div className="mt-6 text-center">
                <button
                  type="button"
                  onClick={requestResume}
                  disabled={busy}
                  data-testid="resume-subscription-btn"
                  className="inline-flex items-center gap-1.5 rounded-full px-5 py-2.5 text-sm font-medium text-white shadow-rose disabled:opacity-60"
                  style={{ background: "linear-gradient(135deg, #C9748A 0%, #B85E75 100%)" }}
                >
                  {busy ? <Loader2 size={14} className="animate-spin" /> : "Keep my Premium"}
                </button>
                <p className="text-[10px] text-cocoa/40 font-light mt-2 max-w-xs mx-auto leading-relaxed">
                  Changed your mind? Undo the cancellation and your subscription keeps renewing.
                </p>
              </div>
            )}

            {/* Confirmation modal — portaled to body so no parent stacking
                context can hide it behind the fixed bottom nav. The nav is
                also hidden via the `gg-modal-open` body class while open. */}
            {showCancel && createPortal(
              <div
                className="fixed inset-0 flex items-end sm:items-center justify-center bg-cocoa/40 backdrop-blur-[2px] p-0 sm:p-4"
                style={{ zIndex: 100001 }}
                data-testid="cancel-modal"
                onClick={() => !cancelBusy && setShowCancel(false)}
              >
                <div
                  className="bg-cream rounded-t-3xl sm:rounded-3xl w-full sm:max-w-sm p-6 shadow-floating overflow-y-auto"
                  style={{
                    maxHeight: "85vh",
                    paddingBottom: "calc(1.5rem + env(safe-area-inset-bottom))",
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="w-12 h-12 rounded-full bg-rose/10 flex items-center justify-center mx-auto mb-3">
                    <ShieldCheck size={20} className="text-rose" />
                  </div>
                  <h3 className="font-serif text-2xl text-cocoa text-center mb-2 leading-tight">
                    Cancel Glow Premium?
                  </h3>
                  <p className="text-sm text-cocoa/70 font-light text-center mb-5 leading-relaxed">
                    Are you sure you want to cancel your Glow &amp; Grow Premium subscription?
                    You&apos;ll lose access to <strong className="font-medium">Glow Report insights</strong>,{" "}
                    <strong className="font-medium">Dupe Finder</strong>,{" "}
                    <strong className="font-medium">Adaptive Routines</strong>, and{" "}
                    <strong className="font-medium">Premium Challenges</strong> at the end of your current
                    billing period.
                  </p>
                  <div className="space-y-2.5">
                    <button
                      type="button"
                      onClick={() => setShowCancel(false)}
                      disabled={cancelBusy}
                      data-testid="cancel-modal-keep"
                      className="w-full rounded-full py-3.5 text-white font-medium shadow-rose disabled:opacity-60"
                      style={{ background: "linear-gradient(135deg, #C9748A 0%, #B85E75 100%)" }}
                    >
                      Keep Premium
                    </button>
                    <button
                      type="button"
                      onClick={requestCancel}
                      disabled={cancelBusy}
                      data-testid="cancel-modal-confirm"
                      className="w-full rounded-full py-3.5 font-medium text-cocoa/70 border border-cocoa/15 hover:bg-white disabled:opacity-60 inline-flex items-center justify-center gap-2"
                    >
                      {cancelBusy && <Loader2 size={14} className="animate-spin" />}
                      Confirm Cancellation
                    </button>
                  </div>
                </div>
              </div>,
              document.body
            )}
          </>
        ) : (
          <>
            {/* Pricing plans */}
            <div className="space-y-3 mb-5" data-testid="pricing-plans">
              <PlanRow
                active={plan === "yearly"}
                onClick={() => setPlan("yearly")}
                title="Yearly access"
                subtitle={`7-day free trial, then ${cur.symbol}${cur.yr}/year · Save ${savings}%`}
                price={`${cur.symbol}${monthlyEquivalent.toFixed(2)}`}
                priceNote="per month"
                bestValue
                testId="plan-yearly"
              />
              <PlanRow
                active={plan === "monthly"}
                onClick={() => setPlan("monthly")}
                title="Monthly access"
                subtitle={`7-day free trial, then ${cur.symbol}${cur.mo}/month`}
                price={`${cur.symbol}${cur.mo}`}
                priceNote="per month"
                testId="plan-monthly"
              />
            </div>

            {/* CTA */}
            <button
              onClick={startCheckout}
              disabled={busy}
              data-testid="checkout-btn"
              className="w-full rounded-full py-4 text-white font-medium text-base flex items-center justify-center gap-2 transition-transform active:scale-[0.98] disabled:opacity-60"
              style={{
                background: "linear-gradient(135deg, #C9748A 0%, #B85E75 100%)",
                boxShadow:
                  "0 6px 20px -4px rgba(201,116,138,0.45), 0 2px 8px rgba(184,94,117,0.35)",
                fontFamily: "'Cormorant Garamond', serif",
                fontSize: 22,
              }}
            >
              {busy ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  Start 7-day free trial <span>✨</span>
                </>
              )}
            </button>

            {/* Trial disclosure — explicit about card + auto-conversion */}
            <p className="text-[11px] text-cocoa/70 font-medium text-center mt-3 leading-relaxed" data-testid="trial-disclosure">
              Cancel anytime before day 7 to avoid being charged.
            </p>

            {/* Legal */}
            <p className="text-[10px] text-cocoa/55 font-light text-center mt-3 leading-relaxed">
              Card required. After 7 days your trial auto-converts to a paid
              subscription unless cancelled. Subscription auto-renews thereafter.
              <br />
              Manage subscriptions in Account Settings.
            </p>
            <p className="text-center mt-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs">
              <Link
                to="/terms"
                className="text-rose font-medium hover:underline"
                data-testid="terms-link"
              >
                Terms
              </Link>
              <Link
                to="/privacy"
                className="text-rose font-medium hover:underline"
                data-testid="privacy-link"
              >
                Privacy
              </Link>
              <Link
                to="/refund"
                className="text-rose font-medium hover:underline"
                data-testid="refund-link"
              >
                Refund policy
              </Link>
              <Link
                to="/contact"
                className="text-rose font-medium hover:underline"
                data-testid="contact-link"
              >
                Contact
              </Link>
            </p>
            <p className="text-[9px] text-cocoa/35 text-center mt-3 uppercase tracking-wider">
              Secure checkout · powered by Paddle
            </p>
          </>
        )}
      </div>
    </div>
  );
}

function PlanRow({ active, onClick, title, subtitle, price, priceNote, bestValue, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className="w-full rounded-2xl bg-white px-5 py-4 flex items-center gap-4 relative text-left transition-all"
      style={{
        border: active ? "2px solid #C9748A" : "1px solid #f0e8e4",
      }}
    >
      {bestValue && (
        <span
          className="absolute -top-2.5 right-4 text-[9px] uppercase tracking-wider px-2.5 py-0.5 rounded-full text-white font-medium"
          style={{ background: "linear-gradient(135deg, #C9975A 0%, #E3BA85 100%)" }}
        >
          Best value
        </span>
      )}
      <div
        className="w-6 h-6 rounded-full flex items-center justify-center shrink-0 transition-all"
        style={{
          background: active ? "#C9748A" : "transparent",
          border: active ? "none" : "2px solid #d6c4bd",
        }}
      >
        {active && <Check size={14} className="text-white" strokeWidth={3} />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-cocoa text-[15px] leading-tight">{title}</p>
        <p className="text-[11px] font-light text-cocoa/55 mt-0.5">{subtitle}</p>
      </div>
      <div className="text-right shrink-0">
        <p
          className="font-serif text-[24px] leading-none"
          style={{ color: active ? "#C9748A" : "#7a6b65" }}
        >
          {price}
        </p>
        <p className="text-[10px] text-cocoa/45 font-light mt-0.5">{priceNote}</p>
      </div>
    </button>
  );
}

/* === Decorative floating product graphics === */
function FloatingGraphics() {
  return (
    <div className="absolute inset-0 pointer-events-none" aria-hidden>
      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 390 380" preserveAspectRatio="none">
        {/* Sparkle dots in gold */}
        <circle cx="40" cy="60" r="2" fill="#C9975A" opacity="0.5" />
        <circle cx="350" cy="80" r="1.5" fill="#C9975A" opacity="0.6" />
        <circle cx="60" cy="280" r="2" fill="#C9975A" opacity="0.4" />
        <circle cx="310" cy="230" r="1.5" fill="#C9975A" opacity="0.55" />
        <circle cx="200" cy="50" r="1" fill="#C9975A" opacity="0.7" />

        {/* Pearl / bubble drops — large soft circles in white at low opacity */}
        <circle cx="55" cy="110" r="18" fill="white" opacity="0.08" />
        <circle cx="335" cy="180" r="22" fill="white" opacity="0.07" />
        <circle cx="20" cy="220" r="14" fill="white" opacity="0.1" />
        <circle cx="370" cy="320" r="20" fill="white" opacity="0.06" />

        {/* Serum drop teardrop top-left */}
        <path
          d="M 70 180 Q 60 192 65 200 Q 70 208 80 200 Q 85 192 80 180 Q 75 172 70 180 Z"
          fill="white"
          opacity="0.12"
        />
        {/* Serum drop teardrop bottom-right */}
        <path
          d="M 320 100 Q 310 113 315 122 Q 322 131 332 122 Q 337 113 332 100 Q 326 92 320 100 Z"
          fill="white"
          opacity="0.13"
        />

        {/* Serum bottle silhouette left side */}
        <g opacity="0.13">
          <rect x="22" y="155" width="22" height="40" rx="4" fill="white" />
          <rect x="27" y="145" width="12" height="10" rx="2" fill="white" />
          <circle cx="33" cy="143" r="3" fill="white" />
        </g>
        {/* Cream jar silhouette right side */}
        <g opacity="0.12">
          <ellipse cx="350" cy="265" rx="20" ry="6" fill="white" />
          <rect x="330" y="265" width="40" height="22" rx="3" fill="white" />
          <ellipse cx="350" cy="287" rx="20" ry="4" fill="white" />
        </g>
        {/* Perfume bottle silhouette center */}
        <g opacity="0.1">
          <rect x="160" y="280" width="28" height="36" rx="2" fill="white" />
          <rect x="167" y="270" width="14" height="10" rx="1" fill="white" />
          <rect x="170" y="262" width="8" height="8" rx="1" fill="white" />
        </g>

        {/* More sparkle dots */}
        <circle cx="155" cy="120" r="1.5" fill="#C9975A" opacity="0.5" />
        <circle cx="250" cy="160" r="1" fill="#C9975A" opacity="0.6" />
        <circle cx="100" cy="340" r="1.5" fill="#C9975A" opacity="0.45" />
        <circle cx="280" cy="350" r="2" fill="#C9975A" opacity="0.5" />
      </svg>
    </div>
  );
}

/* CSS-rendered woman with green mask + gold towel for the Adaptive Routines feature icon. */
function SpaWomanIcon() {
  return (
    <svg width="32" height="32" viewBox="0 0 40 40" aria-hidden>
      {/* Face oval — medium brown */}
      <ellipse cx="20" cy="23" rx="9" ry="11" fill="#C29071" />
      {/* Green face mask covering most of face */}
      <path
        d="M 11 21 Q 11 32 20 33 Q 29 32 29 21 Q 25 18 20 18 Q 15 18 11 21 Z"
        fill="#7FB069"
      />
      {/* Eye holes */}
      <ellipse cx="16.5" cy="22.5" rx="1.3" ry="0.9" fill="#C29071" />
      <ellipse cx="23.5" cy="22.5" rx="1.3" ry="0.9" fill="#C29071" />
      {/* Gold towel wrapped on top of head */}
      <path
        d="M 9 18 Q 10 9 20 8 Q 30 9 31 18 Q 28 15 20 15 Q 12 15 9 18 Z"
        fill="#D4A24C"
      />
      {/* Towel knot on the side */}
      <ellipse cx="28" cy="11" rx="3" ry="2.5" fill="#C9975A" />
      <ellipse cx="29.5" cy="11.5" rx="1.5" ry="1" fill="#E3BA85" />
      {/* Gold sparkle next to face */}
      <path
        d="M 33 25 L 33.7 26.3 L 35 27 L 33.7 27.7 L 33 29 L 32.3 27.7 L 31 27 L 32.3 26.3 Z"
        fill="#C9975A"
      />
    </svg>
  );
}
